import type { Project, ZoneLayoutSettings } from '../gen/ejot/roofing/v1/types';
import { toContract, type GeometryModel } from '../features/roof-plan-editor/model';

export const DEFAULT_ROOF_WIDTH_MM = 40_000;
export const DEFAULT_ROOF_DEPTH_MM = 25_000;
export const DEFAULT_ROOF_HEIGHT_MM = 15_000;
export const DEFAULT_MANUAL_WIND_VELOCITY_MPS = 28;
export const DEFAULT_ELEVATION_ABOVE_SEA_LEVEL_MM = 0;
export const DEFAULT_OVERLAP_WIDTH_MM = 120;
export const DEFAULT_MAX_FIXING_SPACING_MM = 600;
export const DEFAULT_ROLL_WIDTH_MM = 1_000;
export const DEFAULT_CORNER_EDGE_ROLL_WIDTH_MM = 500;
export const DEFAULT_INNER_ROLL_WIDTH_MM = 1_000;
export const DEFAULT_ROLL_LENGTH_MM = 20_000;
export const DEFAULT_END_LAP_WIDTH_MM = 120;
export const DEFAULT_COURSE_START_STAGGER_MM = 5_000;
export const DEVELOPMENT_DESIGN_METHOD_ID = 'cen-ts-17659-en1991-1-4-cen-dev-v1';
export const DEVELOPMENT_DATASET_ID = 'manual-en1991-1-4-cen-dev-v1';
export const DEVELOPMENT_SAFETY_FACTOR_PACKAGE_ID = 'DEV-SF-001';
export const DEVELOPMENT_CATALOGUE_ID = 'development-0.1.1';
export const DEVELOPMENT_MEMBRANE_ID = 'dev-membrane-001';
export const DEVELOPMENT_FIXING_SYSTEM_ID = 'dev-roof-system-001';
export const DEVELOPMENT_SUBSTRATE_PRODUCT_ID = 'substrate-generic-steel-deck';
export const DEVELOPMENT_DEFAULT_COUNTRY_CODE = 'TR';
export const DEVELOPMENT_DEFAULT_NATIONAL_DATASET_ID = 'tr-user-approved-zone-1-2026-08-07';
export const DEVELOPMENT_DEFAULT_WIND_ZONE_ID = 'tr-zone-1';
export const USER_DEFINED_SYSTEM_PREFIX = 'USER_DEFINED:';
export const USER_DEFINED_MEMBRANE_BRAND = 'Membrane Brand 1';
export const USER_DEFINED_MEMBRANE_TYPE = 'PVC';
export const USER_DEFINED_MEMBRANE_THICKNESS_MM = 1.5;
export const USER_DEFINED_FIXING_SYSTEM_NAME = 'User Defined Fixing System';
export const USER_DEFINED_CHARACTERISTIC_RESISTANCE_KN = 1.5;

const DEVELOPMENT_DEFAULT_DECK_PROFILE = {
  sheet_thickness: { millimetres: 0.75, origin: 'VALUE_ORIGIN_DATASET' as const },
  rib_pitch: { millimetres: 183, origin: 'VALUE_ORIGIN_DATASET' as const },
  upper_flange_width: { millimetres: 55, origin: 'VALUE_ORIGIN_DATASET' as const },
  lower_flange_width: { millimetres: 35, origin: 'VALUE_ORIGIN_DATASET' as const },
  profile_depth: { millimetres: 40, origin: 'VALUE_ORIGIN_DATASET' as const },
  orientation: { degrees: 0, origin: 'VALUE_ORIGIN_INPUT' as const },
  minimum_edge_distance: { millimetres: 15, origin: 'VALUE_ORIGIN_DATASET' as const },
};

export function createDefaultGeometry(): GeometryModel {
  return {
    points: [
      { id: 'default-point-1', x: 0, y: 0, locked: false },
      { id: 'default-point-2', x: DEFAULT_ROOF_WIDTH_MM, y: 0, locked: false },
      { id: 'default-point-3', x: DEFAULT_ROOF_WIDTH_MM, y: DEFAULT_ROOF_DEPTH_MM, locked: false },
      { id: 'default-point-4', x: 0, y: DEFAULT_ROOF_DEPTH_MM, locked: false },
    ],
    closed: true,
    openings: [],
    edges: [],
    northOrientationDegrees: 0,
    revision: 0,
  };
}

export function createDefaultCatalogueSelection() {
  return {
    substrateCategory: 'SUBSTRATE_CATEGORY_STEEL_DECK' as const,
    membraneProductId: DEVELOPMENT_MEMBRANE_ID,
    fixingCombinationId: DEVELOPMENT_FIXING_SYSTEM_ID,
    totalInsulationThicknessMm: 100,
    fixingBasis: 'FIXING_RESISTANCE_BASIS_COMPLETE_SYSTEM_DESIGN' as const,
    completeSystemSource: 'CATALOGUE' as const,
  };
}

/**
 * Zone rows contain only genuinely zone-dependent inputs. Shared production
 * and application data live in the synthetic membrane-production record so
 * there is one authoritative roll length / overlap / end-lap / stagger source.
 */
export function createDefaultZoneLayouts(): ZoneLayoutSettings[] {
  const layout = (zoneId: 'F' | 'G' | 'H' | 'I', membraneWidthMillimetres: number): ZoneLayoutSettings => ({
    zone_id: zoneId,
    membrane_width: { millimetres: membraneWidthMillimetres, origin: 'VALUE_ORIGIN_INPUT' },
    maximum_spacing: { millimetres: DEFAULT_MAX_FIXING_SPACING_MM, origin: 'VALUE_ORIGIN_INPUT' },
    additional_fixings_outside_overlap: false,
    pattern_id: 'single-overlap-row',
    installation_direction: { degrees: 0, origin: 'VALUE_ORIGIN_INPUT' },
    fixed_telescope: false,
  });

  const production: ZoneLayoutSettings = {
    zone_id: '__MEMBRANE_PRODUCTION__',
    membrane_width: { millimetres: DEFAULT_ROLL_WIDTH_MM, origin: 'VALUE_ORIGIN_INPUT' },
    roll_length: { millimetres: DEFAULT_ROLL_LENGTH_MM, origin: 'VALUE_ORIGIN_INPUT' },
    overlap_width: { millimetres: DEFAULT_OVERLAP_WIDTH_MM, origin: 'VALUE_ORIGIN_INPUT' },
    end_lap_width: { millimetres: DEFAULT_END_LAP_WIDTH_MM, origin: 'VALUE_ORIGIN_INPUT' },
    course_start_stagger: { millimetres: DEFAULT_COURSE_START_STAGGER_MM, origin: 'VALUE_ORIGIN_INPUT' },
    pattern_id: 'membrane-production:width=0',
  };

  return [
    layout('F', DEFAULT_CORNER_EDGE_ROLL_WIDTH_MM),
    layout('G', DEFAULT_CORNER_EDGE_ROLL_WIDTH_MM),
    layout('H', DEFAULT_INNER_ROLL_WIDTH_MM),
    layout('I', DEFAULT_INNER_ROLL_WIDTH_MM),
    production,
  ];
}

/**
 * Encodes the project-owned User Defined system identity and tested
 * characteristic resistance in the existing fixing-combination identifier.
 * The backend only decodes this format while the complete-system basis is
 * active, so it never becomes a third resistance basis or catalogue record.
 */
export function userDefinedFixingSystemId(
  name: string,
  characteristicResistanceKn = USER_DEFINED_CHARACTERISTIC_RESISTANCE_KN,
): string {
  const safeName = encodeURIComponent(name.trim() || USER_DEFINED_FIXING_SYSTEM_NAME);
  return `${USER_DEFINED_SYSTEM_PREFIX}${safeName}:${characteristicResistanceKn}`;
}

export function parseUserDefinedFixingSystemId(id: string | undefined): {
  name: string;
  characteristicResistanceKn: number;
} | undefined {
  if (!id?.startsWith(USER_DEFINED_SYSTEM_PREFIX)) return undefined;
  const encoded = id.slice(USER_DEFINED_SYSTEM_PREFIX.length);
  const splitAt = encoded.lastIndexOf(':');
  if (splitAt <= 0) return undefined;
  const valueStart = splitAt + 1;
  const parsedResistanceKn = Number(encoded.slice(valueStart));
  if (!Number.isFinite(parsedResistanceKn)) return undefined;
  try {
    return {
      name: decodeURIComponent(encoded.slice(0, splitAt)),
      characteristicResistanceKn: parsedResistanceKn,
    };
  } catch {
    return undefined;
  }
}

export function createUserDefinedMembrane(
  brand = USER_DEFINED_MEMBRANE_BRAND,
  type = USER_DEFINED_MEMBRANE_TYPE,
  thicknessMm = USER_DEFINED_MEMBRANE_THICKNESS_MM,
) {
  return {
    id: 'user-defined-membrane',
    display_name: brand,
    manufacturer: brand,
    membrane_family: type,
    thickness: { millimetres: thicknessMm, origin: 'VALUE_ORIGIN_INPUT' as const },
    nominal_roll_width: { millimetres: DEFAULT_ROLL_WIDTH_MM, origin: 'VALUE_ORIGIN_INPUT' as const },
    default_overlap_width: { millimetres: DEFAULT_OVERLAP_WIDTH_MM, origin: 'VALUE_ORIGIN_INPUT' as const },
    minimum_spacing: { millimetres: 0, origin: 'VALUE_ORIGIN_INPUT' as const },
    maximum_spacing: { millimetres: DEFAULT_MAX_FIXING_SPACING_MM, origin: 'VALUE_ORIGIN_INPUT' as const },
    approval_reference: {
      reference_id: 'USER_DEFINED_COMPLETE_SYSTEM',
      display_text: 'User Defined complete roofing system — project input, no approval implied',
      source_version: 'project-input',
    },
    development_only: true,
  };
}

/**
 * A new development project opens as a coherent executable demonstration case.
 * Turkey Zone 1 supplies the source-backed 28 m/s wind value, while the
 * embedded development catalogue supplies a synthetic complete roofing system
 * solely so wind zones, fixing quantities, drawings and the P0 membrane laying
 * plan can be demonstrated immediately. None of the development product/system
 * records are approved for design or installation.
 */
export function createNewProject(): Project {
  const geometry = createDefaultGeometry();
  return {
    metadata: { project_name: 'Example Roof Project', subject: 'Development demonstration' },
    unit_system: 'UNIT_SYSTEM_METRIC',
    design_method_id: DEVELOPMENT_DESIGN_METHOD_ID,
    safety_factor_package_id: DEVELOPMENT_SAFETY_FACTOR_PACKAGE_ID,
    catalogue_version_id: DEVELOPMENT_CATALOGUE_ID,
    engineering_dataset_version_id: DEVELOPMENT_DEFAULT_NATIONAL_DATASET_ID,
    location: {
      country_code: DEVELOPMENT_DEFAULT_COUNTRY_CODE,
      national_dataset_id: DEVELOPMENT_DEFAULT_NATIONAL_DATASET_ID,
      wind_zone_id: DEVELOPMENT_DEFAULT_WIND_ZONE_ID,
      selection_method: 'LOCATION_SELECTION_METHOD_LIST',
      terrain_category_id: 'terrain-II',
      directional_factor: { value: 1, symbol: 'c_dir', display_precision: 2, origin: 'VALUE_ORIGIN_INPUT' },
      seasonal_factor: { value: 1, symbol: 'c_season', display_precision: 2, origin: 'VALUE_ORIGIN_INPUT' },
      air_density: { value: 1.25, symbol: 'rho', unit: 'kg/m3', display_precision: 3, origin: 'VALUE_ORIGIN_INPUT' },
      elevation_above_sea_level: { millimetres: DEFAULT_ELEVATION_ABOVE_SEA_LEVEL_MM, origin: 'VALUE_ORIGIN_INPUT' },
      north_orientation: { degrees: 0, origin: 'VALUE_ORIGIN_INPUT' },
    },
    modules: [{
      id: 'module-1',
      display_name: 'Roof 1',
      quantity_multiplier: 1,
      roof_height_above_ground: { millimetres: DEFAULT_ROOF_HEIGHT_MM, origin: 'VALUE_ORIGIN_INPUT' },
      roof_slope: { degrees: 2, origin: 'VALUE_ORIGIN_INPUT' },
      build_up: {
        plan: toContract(geometry),
        section: {
          substrate_category: 'SUBSTRATE_CATEGORY_STEEL_DECK',
          substrate_thickness: { millimetres: 0.75, origin: 'VALUE_ORIGIN_DATASET' },
          deck_profile: DEVELOPMENT_DEFAULT_DECK_PROFILE,
          base_insulation_thickness: { millimetres: 100, origin: 'VALUE_ORIGIN_INPUT' },
          total_insulation_thickness: { millimetres: 100, origin: 'VALUE_ORIGIN_DERIVED' },
          anchorage_depth: { millimetres: 20, origin: 'VALUE_ORIGIN_DATASET' },
        },
      },
      internal_pressure: { method: 'INTERNAL_PRESSURE_METHOD_AUTOMATIC' },
      fixing_system: {
        membrane_product_id: DEVELOPMENT_MEMBRANE_ID,
        fixing_combination_id: DEVELOPMENT_FIXING_SYSTEM_ID,
        substrate_product_id: DEVELOPMENT_SUBSTRATE_PRODUCT_ID,
        resistance_basis: 'FIXING_RESISTANCE_BASIS_COMPLETE_SYSTEM_DESIGN',
      },
      zone_layouts: createDefaultZoneLayouts(),
    }],
  };
}
